/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 */

import { goto } from '$app/navigation';
import { page } from '$app/stores';
import { get } from 'svelte/store';
import { getHandler } from '../sharedlib';
import { ensureAssetSummariesLoaded } from '../stores/assets.store';
import { calendarEventStore } from '../stores/calendarEvent.store';
import { refreshResearchItems } from '../stores/conversation.store';
import { isAutoForward } from '../stores/nav.store';
import { refreshOnboardingStatus, validateOnboarding } from '../stores/onboarding.store';
import { safeLoadStoredLinearData, safeRefreshLinearData } from '../stores/provider.linear.store';
import { safeLoadStoredSelectedCalendars } from '../stores/selected.calendars.store';
import { taskStore } from '../stores/task.store';
import { loadStoredThreads, refreshThreads } from '../stores/threads.store';
import { workspaceStore } from '../stores/workspace.store';

export const autoForward = (url: string) => {
  const $page = get(page) as unknown as { url: { pathname: string } };
  if ($page.url.pathname === url) return;
  console.trace('autoForward:', url);
  goto(url);
  isAutoForward.set(true);
};

let updateLayoutInProgress = false;
let lastLayoutPathname: string | undefined = undefined;

export const updateLayout = async (pathname: string | undefined) => {
  if (!pathname) pathname = '';

  // Prevent duplicate concurrent calls for the same pathname
  if (updateLayoutInProgress && lastLayoutPathname === pathname) {
    console.log('[updateLayout] Already in progress for pathname:', pathname, '- skipping');
    return;
  }

  console.log('[updateLayout] Starting for pathname:', pathname);
  updateLayoutInProgress = true;
  lastLayoutPathname = pathname;

  try {
    const isHome = pathname.startsWith('/app/home') || pathname.startsWith('/') || pathname === '';

    // Start all required tasks immediately

    refreshOnboardingStatus();
    // const dbTask = asyncLoadDB();
    const dbTask = Promise.resolve();
    const validationTask = (async () => {
      await validateOnboarding();
      // Removed automatic permission check - will be checked when user anticipates it (onboarding, recording)
    })();
    const workspaceTask = workspaceStore.refreshWorkspaces();

    const requiredTasks: Promise<any>[] = [
      dbTask,
      workspaceTask,
      validationTask,
      getHandler().recordingController.ensureAllDirectories()
    ];

    const calendarsTask = safeLoadStoredSelectedCalendars();

    const calendarEventsTask = dbTask.then(async () => {
      await calendarsTask;
      console.log('[Layout] Starting calendar event store refresh...');
      await calendarEventStore.refreshCalendarEvents();
      console.log('[Layout] Calendar event store refresh completed');
    });

    const tasksTask = dbTask.then(() => taskStore.refreshTasks(true));
    requiredTasks.push(tasksTask);

    if (pathname.startsWith('/app/calendar')) {
      requiredTasks.push(calendarsTask);
      requiredTasks.push(calendarEventsTask);
    }

    const researchTask = refreshResearchItems();
    if (pathname.startsWith('/app/research')) {
      requiredTasks.push(researchTask);
    }

    // Start optional tasks next
    const threadsTask = loadStoredThreads().then(refreshThreads);

    if (isHome) {
      requiredTasks.push(calendarsTask);
      requiredTasks.push(calendarEventsTask);
    } else if (pathname.startsWith('/app/threads')) {
      requiredTasks.push(threadsTask);
    }

    // Load drafts/assets for drafts and drive routes
    if (pathname.startsWith('/app/drafts') || pathname.startsWith('/app/drive')) {
      const draftsTask = dbTask.then(() => ensureAssetSummariesLoaded());
      requiredTasks.push(draftsTask);
    }
    console.log('isHome', isHome, pathname);

    // Refresh Linear provider data in background (non-blocking, low priority)
    setTimeout(() => {
      safeLoadStoredLinearData(false, false)
        .then(() => safeRefreshLinearData())
        .then(() => {
          console.log('[updateLayout] Linear provider data refreshed in background');
        })
        .catch((error: unknown) => {
          console.debug('[updateLayout] Error refreshing Linear provider data:', error);
        });
    }, 100);

    const requiredStart = Date.now();
    const result = await Promise.all(requiredTasks);
    console.log('[updateLayout] Required tasks completed in', `${Date.now() - requiredStart}ms`);
    return result;
  } finally {
    updateLayoutInProgress = false;
  }
};
